import React from 'react';

const BOTTOM_TOLERANCE = 2;

export interface ScrollOverflow {
    hasOverflow: boolean;
    hasMoreBelow: boolean;
}

export function useScrollOverflow(onMeasure?: (state: ScrollOverflow) => void) {
    // Callback refs also detect content mounted later, such as inside a dialog.
    const [parent, parentRef] = React.useState<HTMLElement | null>(null);
    const [content, contentRef] = React.useState<HTMLElement | null>(null);
    const [overflow, setOverflow] = React.useState<ScrollOverflow>({
        hasOverflow: false,
        hasMoreBelow: false,
    });

    // Consumers can reset their scroll position in a layout effect before measurement.
    React.useEffect(() => {
        if (!parent || !content) {
            return;
        }

        const measure = () => {
            if (parent.clientHeight === 0) {
                return;
            }
            /*
             * scrollHeight is the entire content:
             * ┌──────────────────────────┐ ┐
             * │ Already passed           │ │ scrollTop
             * ├──────────────────────────┤ │
             * │ Visible viewport         │ │ clientHeight
             * ├──────────────────────────┤ │
             * │ Content still below      │ │
             * └──────────────────────────┘ ┘ scrollHeight
             *
             * At the bottom: scrollTop + clientHeight ≈ scrollHeight.
             * hasMoreBelow is the inverse, allowing a 2px tolerance at the bottom.
             */
            const next = {
                hasOverflow: parent.scrollHeight > parent.clientHeight + 1,
                hasMoreBelow:
                    parent.scrollTop + parent.clientHeight <
                    parent.scrollHeight - BOTTOM_TOLERANCE,
            };
            setOverflow(previous =>
                previous.hasOverflow === next.hasOverflow &&
                previous.hasMoreBelow === next.hasMoreBelow
                    ? previous
                    : next
            );
            // Consumers can react to a fresh measurement after replacing their content.
            onMeasure?.(next);
        };
        let frame = 0;
        const scheduleMeasure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        };
        const observer = new ResizeObserver(scheduleMeasure);
        observer.observe(parent);
        observer.observe(content);
        parent.addEventListener('scroll', scheduleMeasure, { passive: true });
        measure();

        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
            parent.removeEventListener('scroll', scheduleMeasure);
        };
    }, [parent, content, onMeasure]);

    return { parent, content, parentRef, contentRef, ...overflow };
}
